import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Menu, Phone, MessageCircle, MapPin, Clock, Video, CreditCard, Shield, Car, ShoppingCart, FileText, Wrench, Heart, Search, Users, Package, Settings, Gauge, Handshake, HandCoins, X, Navigation, ChevronDown, Layout } from 'lucide-react';
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { Link } from 'react-router-dom';
import { ConfigModal } from './ConfigModal';
import { ServiceModal } from './ServiceModal';
import { StoreSelectionModal } from './StoreSelectionModal';
import { LayoutSelectionModal } from './LayoutSelectionModal';
import { AccountIdModal } from './AccountIdModal';
import { useAppData } from '@/contexts/AppDataContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useStore } from '@/contexts/StoreContext';
import { secureGetItem, secureSetItem } from '@/lib/storage';
import { openWhatsApp } from '@/lib/whatsapp';
import { getAccountId } from '@/lib/obfuscate';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { requestUserLocation, getDistanceToStore, formatDistance } from '@/lib/geolocation';
import { getStoreBrandAssets } from '@/lib/storeBrand';
import { HeaderBrandLogo } from '@/components/HeaderBrandLogo';

// Import service images
import videoCall from '@/assets/services/videoCall.png';
import valuation from '@/assets/services/valuation.png';
import consignment from '@/assets/services/consignment.png';
import consortium from '@/assets/services/consortium.png';
import forwardingAgent from '@/assets/services/forwardingAgent.png';
import financing from '@/assets/services/financing.png';
import partsAccessories from '@/assets/services/partsAccessories.png';
import scheduleReview from '@/assets/services/scheduleReview.png';
import insurance from '@/assets/services/insurance.png';
import scheduleServices from '@/assets/services/scheduleServices.png';
import fipeTable from '@/assets/services/fipeTable.png';
import testdrive from '@/assets/services/testdrive.png';
import directSales from '@/assets/services/directSales.png';
import { CardContent, CardDescription } from './ui/card';
import { openStoreDirections } from '@/lib/storeMapUrls';

interface HeaderProps {
  config?: any;
}

export const Header = ({ config: propConfig }: HeaderProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isHoverCardOpen, setIsHoverCardOpen] = useState(false);
  const [isServicesDropdownOpen, setIsServicesDropdownOpen] = useState(false);
  const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
  const [selectedDesktopService, setSelectedDesktopService] = useState<any>(null);
  const [isMobileServiceModalOpen, setIsMobileServiceModalOpen] = useState(false);
  const [selectedMobileService, setSelectedMobileService] = useState<any>(null);
  const [isStoreModalOpen, setIsStoreModalOpen] = useState(false);
  const [isLayoutModalOpen, setIsLayoutModalOpen] = useState(false);
  const [isAccountIdModalOpen, setIsAccountIdModalOpen] = useState(false);
  const [favoritesCount, setFavoritesCount] = useState(0);
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const { config, vehicles, accountId } = useAppData();
  const { colors } = useTheme();
  const { selectedStoreIndex, setSelectedStoreIndex, availableStores, selectedStore } = useStore();

  // Get original account ID from environment (for button visibility check)
  const originalAccountId = getAccountId();
  const shouldShowDevButtons = originalAccountId === '677e7e1b763f79265b9228db';

  // Use context config if no prop config is provided
  const currentConfig = propConfig || config;

  // Use selected store from context
  const store = selectedStore || currentConfig.stores[0];

  // Check if should show store selector (more than 1 available store)
  const showStoreSelector = availableStores.length > 1;

  // Update favorites count - only count vehicles that still exist
  useEffect(() => {
    const updateFavoritesCount = async () => {
      try {
        const favoriteIds = await secureGetItem('favorites');

        if (favoriteIds && vehicles.length > 0 && Array.isArray(favoriteIds)) {
          // Filter to only count favorites that still exist in the vehicle list
          const existingFavorites = favoriteIds.filter(id =>
            vehicles.some(v => v.id === id)
          );

          setFavoritesCount(existingFavorites.length);

          // Clean up localStorage if there are sold vehicles
          if (existingFavorites.length !== favoriteIds.length) {
            await secureSetItem('favorites', existingFavorites);
          }
        } else {
          setFavoritesCount(0);
        }
      } catch (error) {
        setFavoritesCount(0);
      }
    };

    // Initial count
    updateFavoritesCount();

    // Listen for favorites updates
    window.addEventListener('favoritesUpdated', updateFavoritesCount);
    window.addEventListener('storage', updateFavoritesCount);

    return () => {
      window.removeEventListener('favoritesUpdated', updateFavoritesCount);
      window.removeEventListener('storage', updateFavoritesCount);
    };
  }, [vehicles]);

  const headerColor = colors.header;
  const menuColor = colors.menu;
  const primaryColor = colors.primary;
  const secondaryColor = colors.secondary;
  const buttonColor = colors.button;

  // Format opening hours for display in header - groups consecutive days with same hours
  const getFormattedHours = () => {
    const hours = store?.openingHours?.default;
    if (!hours) return 'Seg-Sex: 8h-19h | Sáb: 8h-18h';

    const daysOrder = [
      { key: 'monday', short: 'Seg' },
      { key: 'tuesday', short: 'Ter' },
      { key: 'wednesday', short: 'Qua' },
      { key: 'thursday', short: 'Qui' },
      { key: 'friday', short: 'Sex' },
      { key: 'saturday', short: 'Sáb' },
      { key: 'sunday', short: 'Dom' }
    ];

    // Group consecutive days with same hours
    const groups: Array<{ days: string[]; time: string }> = [];
    let currentGroup: { days: string[]; time: string } | null = null;

    daysOrder.forEach((day) => {
      const dayHours = hours[day.key];
      const timeStr = dayHours?.from && dayHours?.to
        ? `${dayHours.from}-${dayHours.to}`
        : 'Fechado';

      if (!currentGroup) {
        // Start new group
        currentGroup = { days: [day.short], time: timeStr };
      } else if (currentGroup.time === timeStr) {
        // Same hours - add to current group
        currentGroup.days.push(day.short);
      } else {
        // Different hours - save current group and start new one
        groups.push(currentGroup);
        currentGroup = { days: [day.short], time: timeStr };
      }
    });

    // Add last group
    if (currentGroup) {
      groups.push(currentGroup);
    }

    // Format groups for display (limit to 3 groups for header space)
    const formattedGroups = groups.slice(0, 3).map(group => {
      const dayRange = group.days.length > 1
        ? `${group.days[0]} à ${group.days[group.days.length - 1]}`
        : group.days[0];
      return `${dayRange}: ${group.time}`;
    });

    if (formattedGroups.length === 0) {
      return 'Consulte horários';
    }

    return formattedGroups.join(' | ');
  };

  const formattedHours = getFormattedHours();

  // Request user location only when needed (user interaction)
  // Don't request automatically on page load to avoid trust issues

  // Get main service info
  const mainServiceId = store.mainService;
  const mainService = store.services?.find((s: any) => s.service === mainServiceId);
  const hasBanners = store.banners && store.banners.length > 0;

  const serviceInfo: Record<string, { icon: any; title: string; description: string; image: string; color: string }> = {
    '65da42048eca108d87c91da6': { icon: Video, title: 'Agendar Videochamada', description: 'Agende uma videochamada para conhecer nossos veículos sem sair de casa', image: videoCall, color: 'bg-blue-500' },
    '65dcb7548eca108d87c91dbb': { icon: Search, title: 'Avaliação', description: 'Avaliação profissional do seu veículo com preço justo de mercado', image: valuation, color: 'bg-green-500' },
    '65dcb7c58eca108d87c91dbd': { icon: HandCoins, title: 'Consignação', description: 'Deixe seu veículo em consignação conosco e venda com segurança', image: consignment, color: 'bg-purple-500' },
    '65dcef468eca108d87c91dc8': { icon: Users, title: 'Consórcio', description: 'Adquira seu veículo através de consórcio com as melhores condições', image: consortium, color: 'bg-teal-500' },
    '65dcb6528eca108d87c91db9': { icon: FileText, title: 'Despachante', description: 'Serviços de despachante para documentação veicular completa', image: forwardingAgent, color: 'bg-orange-500' },
    '65dcb7898eca108d87c91dbc': { icon: CreditCard, title: 'Financiamento', description: 'Análise de crédito e financiamento com as melhores taxas do mercado', image: financing, color: 'bg-blue-600' },
    '6628fd535bda2ae51f6fc5ff': { icon: Package, title: 'Peças e Acessórios', description: 'Peças originais e acessórios para seu veículo', image: partsAccessories, color: 'bg-gray-600' },
    '65dcef098eca108d87c91dc7': { icon: Wrench, title: 'Revisões', description: 'Revisões preventivas e corretivas com equipe especializada', image: scheduleReview, color: 'bg-slate-600' },
    '65dcb7238eca108d87c91dba': { icon: Shield, title: 'Seguros', description: 'Cotação de seguros com as principais seguradoras do país', image: insurance, color: 'bg-red-500' },
    '65dceeac8eca108d87c91dc6': { icon: Settings, title: 'Serviços', description: 'Serviços automotivos especializados para seu veículo', image: scheduleServices, color: 'bg-indigo-600' },
    '65dcb7fe8eca108d87c91dbe': { icon: FileText, title: 'Tabela FIPE', description: 'Consulte o valor do seu veículo pela tabela FIPE', image: fipeTable, color: 'bg-cyan-500' },
    '65dcb5e78eca108d87c91db8': { icon: Gauge, title: 'Test-Drive', description: 'Agende um test drive e experimente o veículo dos seus sonhos', image: testdrive, color: 'bg-indigo-500' },
    '65dcb8548eca108d87c91dbf': { icon: Handshake, title: 'Vendas Diretas', description: 'Vendas diretas com as melhores condições e atendimento personalizado', image: directSales, color: 'bg-green-600' }
  };

  const mainServiceInfo = mainServiceId ? serviceInfo[mainServiceId] : null;
  const MainServiceIcon = mainServiceInfo?.icon || MessageCircle;
  const mainServiceTitle = mainServiceInfo?.title || 'Fale Conosco';

  // Get service image (from config or default)
  const mainServiceImage = mainService?.image || mainServiceInfo?.image;

  // Helper function to get service info
  const getServiceInfo = (serviceId: string) => {
    return serviceInfo[serviceId] || {
      icon: MessageCircle,
      title: 'Serviço',
      description: 'Entre em contato para mais informações',
      image: '',
      color: 'bg-primary'
    };
  };

  const getServiceButtonText = (serviceId: string) => {
    const buttonTexts: Record<string, string> = {
      '65da42048eca108d87c91da6': 'Agendar',
      '65dceeac8eca108d87c91dc6': 'Agendar',
      '65dcef098eca108d87c91dc7': 'Agendar',
      '65dcb5e78eca108d87c91db8': 'Agendar',
      '65dcb7898eca108d87c91dbc': 'Simular',
      '65dcb7548eca108d87c91dbb': 'Avaliar',
      '65dcb7c58eca108d87c91dbd': 'Avaliar',
      '65dcb7238eca108d87c91dba': 'Cotar',
      '65dcb6528eca108d87c91db9': 'Cotar',
      '6628fd535bda2ae51f6fc5ff': 'Cotar',
      '65dcef468eca108d87c91dc8': 'Solicitar',
      '65dcb8548eca108d87c91dbf': 'Solicitar',
      '65dcb7fe8eca108d87c91dbe': 'Consultar',
    };
    return buttonTexts[serviceId] || 'Solicitar';
  };

  // Check if vehicles exist for each category
  const hasSeminovos = vehicles.some(v => !v.isZeroKm && v.mileage > 0 && !v.armored);
  const hasZeroKm = vehicles.some(v => v.isZeroKm || v.mileage === 0);
  const hasBlindados = vehicles.some(v => v.armored);

  // Count how many categories we have
  const categoryCount = [hasSeminovos, hasZeroKm, hasBlindados].filter(Boolean).length;

  const menuItems = [
    { label: 'Início', href: '/', isLink: true },
    { label: 'Estoque', href: '/estoque', isLink: true },
  ];

  // Add conditional menu items only if we have 2 or more categories
  if (categoryCount >= 2) {
    if (hasSeminovos) {
      menuItems.push({ label: 'Seminovos', href: '/estoque?type=seminovo', isLink: true });
    }
    if (hasZeroKm) {
      menuItems.push({ label: 'Zero KM', href: '/estoque?type=zerokm', isLink: true });
    }
    if (hasBlindados) {
      menuItems.push({ label: 'Blindados', href: '/estoque?armored=true', isLink: true });
    }
  }

  // Services for dropdown - TODOS os serviços
  const servicesForMenu = store?.services || [];

  // Handle desktop service click
  const handleDesktopServiceClick = (service: any) => {
    setSelectedDesktopService(service);
    setIsServicesDropdownOpen(false);
    setTimeout(() => {
      setIsServiceModalOpen(true);
    }, 100);
  };

  // Handle mobile service click
  const handleMobileServiceClick = (service: any) => {
    setSelectedMobileService(service);
    setIsOpen(false); // Close mobile menu
    setTimeout(() => {
      setIsMobileServiceModalOpen(true);
    }, 100);
  };

  const { hasLogo, headerBrandSrc: brandImageSrc } = getStoreBrandAssets(currentConfig, store);

  const renderBrandMark = (variant: 'nav' | 'sheet' = 'nav') => {
    if (!brandImageSrc) return null;

    if (hasLogo) {
      return (
        <HeaderBrandLogo
          src={brandImageSrc}
          alt={currentConfig.name}
          variant={variant}
        />
      );
    }

    return (
      <div className="flex items-center justify-center shrink-0">
        <img
          src={brandImageSrc}
          alt={currentConfig.name}
          className={`object-contain rounded-xl ${variant === 'sheet' ? 'w-10 h-10' : 'w-10 h-10 lg:w-11 lg:h-11'}`}
        />
      </div>
    );
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 border-b border-gray-200" style={{ backgroundColor: menuColor, borderColor: `${headerColor}ee` }}>
        {/* Top Bar */}
        <div className="border-b border-gray-100" style={{ borderColor: `${headerColor}ee` }}>
          <div className="bg-gray-50" style={{ backgroundColor: `${headerColor}ee` }}>
            <div className="container mx-auto px-4">
              <div className="flex items-center justify-between text-xs py-2">
                {/* Left: Horário */}
                <div className="hidden md:flex items-center gap-2 text-gray-600" style={{ color: menuColor }}>
                  <Clock className="h-3.5 w-3.5" />
                  <span className="font-medium">{formattedHours}</span>
                </div>

                {/* Right: Contatos */}
                <div className="flex items-center gap-4 ml-auto text-gray-600" style={{ color: menuColor }}>
                  {/* Store Selector - se múltiplas lojas */}
                  {showStoreSelector && (
                    <button
                      onClick={() => setIsStoreModalOpen(true)}
                      className="hidden md:flex items-center gap-1.5 px-2 py-1 rounded hover:bg-gray-100 transition-colors text-xs font-medium"
                    >
                      <MapPin className="h-3 w-3" />
                      <span className="truncate max-w-[120px]">{store.company || `Loja ${selectedStoreIndex + 1}`}</span>
                      <ChevronDown className="h-3 w-3" />
                    </button>
                  )}

                  <a
                    href={store.phone?.ios}
                    className="flex items-center gap-1.5 hover:text-primary transition-colors font-medium"
                    aria-label={store.phone?.formatted ? `Ligar para ${store.phone.formatted}` : 'Ligar para a loja'}
                  >
                    <Phone className="h-3.5 w-3.5" />
                    <span className="hidden md:inline">{store.phone?.formatted}</span>
                  </a>

                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      openWhatsApp(store.whatsapp?.ios);
                    }}
                    className="flex items-center gap-1.5 hover:text-primary transition-colors font-medium"
                    aria-label={store.whatsapp?.formatted ? `Conversar no WhatsApp ${store.whatsapp.formatted}` : 'Conversar no WhatsApp'}
                  >
                    <MessageCircle className="h-3.5 w-3.5" />
                    <span className="hidden md:inline">WhatsApp</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Header */}
        <div className="bg-white" style={{ backgroundColor: headerColor }}>
          <div className="container mx-auto px-4 py-3">
            <div className="flex items-center justify-between gap-6" style={{ height: 66 }}>

              {/* Logo */}
              <Link to="/" className="flex items-center flex-shrink-0 gap-2.5 min-w-0">
                {renderBrandMark('nav')}
                {!hasLogo && (
                  <div className="hidden sm:block min-w-0">
                    <h1 className="text-lg md:text-xl font-bold leading-tight truncate" style={{ color: colors.menu }}>{currentConfig.name}</h1>
                    {currentConfig.slogan && (
                      <p className="hidden md:block text-xs truncate" style={{ color: colors.menu, opacity: 0.75 }}>{currentConfig.slogan}</p>
                    )}
                  </div>
                )}
              </Link>

              {/* Desktop Navigation - Centralizado */}
              <nav className="hidden lg:flex items-center gap-6 flex-1 justify-center">
                {menuItems.map((item) => (
                  item.isLink ? (
                    <Link
                      key={item.label}
                      to={item.href}
                      style={{ color: menuColor }}
                      className="text-sm font-semibold text-gray-700 hover:text-primary transition-colors px-2 py-2 relative group"
                    >
                      {item.label}
                      <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary transition-all duration-300 group-hover:w-full" style={{ backgroundColor: buttonColor }}></span>
                    </Link>
                  ) : (
                    <a
                      key={item.label}
                      href={item.href}
                      style={{ color: menuColor }}
                      className="text-sm font-semibold text-gray-700 hover:text-primary transition-colors px-2 py-2 relative group"
                    >
                      {item.label}
                      <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary transition-all duration-300 group-hover:w-full" style={{ backgroundColor: buttonColor }}></span>
                    </a>
                  )
                ))}

                {/* Serviços Dropdown */}
                {servicesForMenu.length > 0 && (
                  <HoverCard open={isServicesDropdownOpen} onOpenChange={setIsServicesDropdownOpen} openDelay={100} closeDelay={200}>
                    <HoverCardTrigger asChild>
                      <button className="text-sm font-semibold text-gray-700 hover:text-primary transition-colors px-2 py-2 flex items-center gap-1 relative group"
                        style={{ color: menuColor }}>
                        Serviços
                        <ChevronDown className="h-3.5 w-3.5" />
                        <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary transition-all duration-300 group-hover:w-full" style={{ backgroundColor: buttonColor }}></span>
                      </button>
                    </HoverCardTrigger>
                    <HoverCardContent
                      className="w-[320px] p-0 overflow-hidden border border-gray-200  max-h-[500px] overflow-y-auto"
                      side="bottom"
                      align="center"
                      style={{
                        scrollbarWidth: 'thin',
                        scrollbarColor: 'hsl(var(--muted-foreground) / 0.3) transparent',
                      }}
                    >
                      <div className="bg-white">
                        <div className="py-2">
                          {servicesForMenu.map((service: any) => {
                            const info = getServiceInfo(service.service);
                            const IconComponent = info.icon;
                            return (
                              <button
                                key={service.service}
                                className="w-full flex items-start gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left"
                                onClick={() => handleDesktopServiceClick(service)}
                              >
                                <div className="flex-shrink-0">
                                  <IconComponent className="h-5 w-5" style={{ color: primaryColor }} />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-semibold text-gray-900 mb-1">{info.title}</div>
                                  <div className="text-xs text-gray-600 leading-relaxed">{info.description}</div>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </HoverCardContent>
                  </HoverCard>
                )}

                {/* Contato - depois de Serviços */}
                <Link
                  to="/contato"
                  className="text-sm font-semibold text-gray-700 hover:text-primary transition-colors px-2 py-2 relative group"
                  style={{ color: menuColor }}
                >
                  Contato
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary transition-all duration-300 group-hover:w-full" style={{ backgroundColor: buttonColor }}></span>
                </Link>
              </nav>

              {/* Actions - Direita */}
              <div className="hidden lg:flex items-center gap-3">

                {/* Favoritos */}
                <Link to="/favoritos" className="relative">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="relative hover:bg-gray-50 text-gray-700 hover:text-primary font-semibold"
                    style={{
                      color: menuColor,
                      ['--hover-bg' as any]: `${menuColor}10`
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = `${menuColor}10`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = ``;
                    }}
                  >
                    Favoritos
                    {favoritesCount > 0 && (
                      <span
                        className="ml-1.5 flex absolute top-[0px] right-[0px] items-center justify-center min-w-[18px] h-[18px] text-[9px] font-bold text-white rounded-full px-1"
                        style={{ backgroundColor: buttonColor }}
                      >
                        {favoritesCount > 99 ? '99+' : favoritesCount}
                      </span>
                    )}
                  </Button>
                </Link>

                {/* CTA Principal - Invertido */}
                {mainService && hasBanners ? (
                  <>
                    <HoverCard open={isHoverCardOpen} onOpenChange={setIsHoverCardOpen} openDelay={200}>
                      <HoverCardTrigger asChild>
                        <Button
                          variant="outline"
                          className="font-semibold transition-all border hover:bg-gray-50"
                          style={{
                            background: headerColor,
                            borderColor: menuColor,
                            color: menuColor,
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = menuColor;
                            e.currentTarget.style.color = headerColor;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = headerColor;
                            e.currentTarget.style.color = menuColor;
                          }}
                        >
                          {mainServiceTitle}
                        </Button>
                      </HoverCardTrigger>
                      <HoverCardContent className="w-[320px] p-0 overflow-hidden border-0" side="bottom" align="end">
                        {mainServiceInfo && (
                          <div
                            className="group/service cursor-pointer transition-all duration-300 border border-transparent rounded-lg overflow-hidden"
                            onClick={() => {
                              setIsHoverCardOpen(false);
                              setIsServiceModalOpen(true);
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.borderColor = buttonColor;
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.borderColor = 'transparent';
                            }}
                          >
                            <div className="relative aspect-[280/100] overflow-hidden">
                              <img
                                src={mainServiceImage}
                                alt={mainServiceInfo.title}
                                className="w-full h-full object-cover group-hover/service:scale-110 transition-transform duration-700"
                              />
                              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent group-hover/service:from-black/70 transition-colors duration-500" />
                              <div className="absolute top-3 right-3">
                                <div className={`w-10 h-10 ${mainServiceInfo.color} rounded-lg flex items-center justify-center bg-white/20 backdrop-blur-sm group-hover/service:scale-110 transition-transform duration-500`}>
                                  <MainServiceIcon className="h-5 w-5 text-white" />
                                </div>
                              </div>
                              <div className="absolute bottom-3 left-3 right-3">
                                <h3 className="text-white font-bold text-sm">
                                  {mainServiceInfo.title}
                                </h3>
                              </div>
                            </div>

                            {/* Content */}
                            <CardContent className="p-4 flex flex-col flex-grow">
                              <CardDescription className="text-muted-foreground">
                                {mainServiceInfo.description}
                              </CardDescription>
                            </CardContent>
                          </div>
                        )}
                      </HoverCardContent>
                    </HoverCard>
                    {/* Modal para serviço selecionado no dropdown (desktop) ou serviço principal */}
                    <ServiceModal
                      service={selectedDesktopService || mainService}
                      config={currentConfig}
                      isOpen={isServiceModalOpen}
                      onOpenChange={(open) => {
                        setIsServiceModalOpen(open);
                        if (!open) {
                          setSelectedDesktopService(null);
                        }
                      }}
                      serviceImage={(selectedDesktopService?.image) || (selectedDesktopService ? getServiceInfo(selectedDesktopService.service)?.image : mainServiceImage)}
                    />
                  </>
                ) : (
                  <Button
                    variant="outline"
                    className="font-semibold transition-all border-2 hover:bg-gray-50"
                    style={{
                      borderColor: buttonColor,
                      color: buttonColor,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = buttonColor;
                      e.currentTarget.style.color = 'white';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '';
                      e.currentTarget.style.color = buttonColor;
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      openWhatsApp(store.whatsapp?.ios);
                    }}
                  >
                    Fale Conosco
                  </Button>
                )}

                {/* Layout Selection Button - Only show for specific accountId */}
                {shouldShowDevButtons && (
                  <>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="relative hover:bg-gray-50"
                      style={{
                        color: menuColor,
                        ['--hover-bg' as any]: `${menuColor}10`
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = `${menuColor}10`;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = ``;
                      }}
                      onClick={() => setIsLayoutModalOpen(true)}
                      title="Selecionar Layout"
                      aria-label="Selecionar layout"
                    >
                      <Layout className="h-5 w-5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="relative hover:bg-gray-50"
                      style={{
                        color: menuColor,
                        ['--hover-bg' as any]: `${menuColor}10`
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = `${menuColor}10`;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = ``;
                      }}
                      onClick={() => setIsAccountIdModalOpen(true)}
                      title="Alterar ID da Conta"
                      aria-label="Alterar ID da conta"
                    >
                      <Settings className="h-5 w-5" />
                    </Button>
                  </>
                )}
              </div>

              {/* Mobile Menu */}
              <Sheet open={isOpen} onOpenChange={setIsOpen}>
                <SheetTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="lg:hidden hover:bg-white/10 p-2 rounded-full transition-all"
                    aria-label="Abrir menu"
                  >
                    <Menu className="h-6 w-6 text-gray-700" style={{ color: menuColor }} />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-full p-0 border-0 bg-white [&>button]:hidden">
                  <div className="flex flex-col h-full">

                    {/* Header Mobile */}
                    <SheetHeader className="px-4 py-5 border-b border-gray-100" style={{ backgroundColor: primaryColor }}>
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                          {renderBrandMark('sheet')}
                          <div className="text-left min-w-0">
                            <SheetTitle className="text-white text-base font-bold">
                              {currentConfig.name}
                            </SheetTitle>
                            {currentConfig.slogan && (
                              <p className="text-xs mt-0.5 text-white/75">{currentConfig.slogan}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {shouldShowDevButtons && (
                            <>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-white hover:bg-white/15 rounded-full"
                                onClick={() => {
                                  setIsOpen(false);
                                  setIsLayoutModalOpen(true);
                                }}
                                aria-label="Selecionar layout (mobile)"
                                title="Selecionar Layout"
                              >
                                <Layout className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-white hover:bg-white/15 rounded-full"
                                onClick={() => {
                                  setIsOpen(false);
                                  setIsAccountIdModalOpen(true);
                                }}
                                aria-label="Alterar ID da conta (mobile)"
                                title="Alterar ID da Conta"
                              >
                                <Settings className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-white hover:bg-white/10 rounded-full"
                            onClick={() => setIsOpen(false)}
                            aria-label="Fechar menu"
                          >
                            <X className="h-5 w-5" />
                          </Button>
                        </div>
                      </div>
                    </SheetHeader>

                    {/* Menu Items */}
                    <div className="flex-1 overflow-y-auto bg-white">
                      <nav className="py-2">
                        {menuItems.map((item) => (
                          <div key={item.label}>
                            {item.isLink ? (
                              <Link
                                to={item.href}
                                className="flex items-center justify-between px-6 py-3.5 text-gray-900 hover:bg-gray-50 transition-colors font-medium"
                                onClick={() => setIsOpen(false)}
                              >
                                {item.label}
                              </Link>
                            ) : (
                              <a
                                href={item.href}
                                className="flex items-center justify-between px-6 py-3.5 text-gray-900 hover:bg-gray-50 transition-colors font-medium"
                                onClick={() => setIsOpen(false)}
                              >
                                {item.label}
                              </a>
                            )}
                          </div>
                        ))}

                        {/* Serviços no Mobile - apenas label, serviços já estão em Contato > Ações Rápidas */}
                        {servicesForMenu.length > 0 && (
                          <div className="px-6 py-2">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                              Serviços
                            </p>
                          </div>
                        )}

                        {/* Lista de Serviços no Mobile */}
                        {servicesForMenu.map((service: any) => {
                          const info = getServiceInfo(service.service);
                          const IconComponent = info.icon;
                          return (
                            <button
                              key={service.service}
                              className="w-full flex items-center gap-3 px-6 py-3 text-gray-900 hover:bg-gray-50 transition-colors font-medium"
                              onClick={() => handleMobileServiceClick(service)}
                            >
                              <IconComponent className="h-5 w-5 text-primary" />
                              <span>{info.title}</span>
                            </button>
                          );
                        })}

                        {/* Contato no Mobile */}
                        <Link
                          to="/contato"
                          className="flex items-center px-6 py-3.5 text-gray-900 hover:bg-gray-50 transition-colors font-medium"
                          onClick={() => setIsOpen(false)}
                        >
                          Contato
                        </Link>
                      </nav>

                      <Separator className="my-2" />

                      <div className="py-2">
                        <Link
                          to="/favoritos"
                          className="flex items-center justify-between px-6 py-3.5 text-gray-900 hover:bg-gray-50 transition-colors font-medium"
                          onClick={() => setIsOpen(false)}
                        >
                          <div className="flex items-center gap-3">
                            <Heart className="h-5 w-5 text-primary" />
                            <span>Favoritos</span>
                          </div>
                          {favoritesCount > 0 && (
                            <span
                              className="text-xs font-bold text-white px-2 py-1 rounded-full"
                              style={{ backgroundColor: buttonColor }}
                            >
                              {favoritesCount}
                            </span>
                          )}
                        </Link>
                      </div>

                      <Separator className="my-2" />

                      {/* Contatos */}
                      <div className="py-2">
                        <div className="px-6 py-2">
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                            Contato
                          </p>
                        </div>

                        <button
                          className="w-full flex items-center gap-3 px-6 py-3.5 text-gray-900 hover:bg-gray-50 transition-colors font-medium"
                          onClick={() => {
                            openWhatsApp(store.whatsapp?.ios);
                            setIsOpen(false);
                          }}
                        >
                          <MessageCircle className="h-5 w-5 text-primary" />
                          <span>WhatsApp</span>
                        </button>

                        <button
                          className="w-full flex items-center gap-3 px-6 py-3.5 text-gray-900 hover:bg-gray-50 transition-colors font-medium"
                          onClick={() => {
                            window.open(store.phone?.ios, '_blank');
                            setIsOpen(false);
                          }}
                        >
                          <Phone className="h-5 w-5 text-primary" />
                          <span>Ligar</span>
                        </button>

                        <button
                          className="w-full flex items-center gap-3 px-6 py-3.5 text-gray-900 hover:bg-gray-50 transition-colors font-medium"
                          onClick={async () => {
                            // Request location only when user clicks
                            if (!userLocation) {
                              const location = await requestUserLocation();
                              if (location) {
                                setUserLocation(location);
                              }
                            }
                            const place = store.place;
                            openStoreDirections(place);
                            setIsOpen(false);
                          }}
                        >
                          <MapPin className="h-5 w-5 text-primary" />
                          <span>Como Chegar</span>
                        </button>
                      </div>
                    </div>

                    {/* Footer CTA */}
                    {mainService && hasBanners && (
                      <div className="p-4 border-t bg-gray-50">
                        <Button
                          className="w-full text-white font-semibold "
                          size="lg"
                          style={{
                            background: `linear-gradient(135deg, ${buttonColor}, ${buttonColor}ee)`,
                          }}
                          onClick={() => handleMobileServiceClick(mainService)}
                        >
                          <MainServiceIcon className="h-5 w-5 mr-2" />
                          {mainServiceTitle}
                        </Button>
                      </div>
                    )}
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>

        {/* Store Selection Modal */}
        {showStoreSelector && (
          <StoreSelectionModal
            config={currentConfig}
            isOpen={isStoreModalOpen}
            onClose={() => setIsStoreModalOpen(false)}
          />
        )}

        {/* Layout Selection Modal */}
        {shouldShowDevButtons && (
          <LayoutSelectionModal
            isOpen={isLayoutModalOpen}
            onOpenChange={setIsLayoutModalOpen}
          />
        )}

        {/* Account ID Modal */}
        {shouldShowDevButtons && (
          <AccountIdModal
            isOpen={isAccountIdModalOpen}
            onOpenChange={setIsAccountIdModalOpen}
          />
        )}
      </header>

      {/* Mobile Service Modal - Rendered outside header via portal */}
      {selectedMobileService && createPortal(
        <ServiceModal
          service={selectedMobileService}
          config={currentConfig}
          serviceImage={selectedMobileService.image || getServiceInfo(selectedMobileService.service)?.image}
          isOpen={isMobileServiceModalOpen}
          onOpenChange={setIsMobileServiceModalOpen}
        />,
        document.body
      )}
    </>
  );
};